import React, { useRef, useState } from 'react';
import cx from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { navigate } from '@reach/router';
import 'swiper/css';
import 'swiper/css/navigation';

import TopicPill from 'components/TopicPill/TopicPill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import useMobile from 'hooks/useMobile';
import useSearchStore from 'stores/SearchStoreV2';

const TopicListItem = ({
  topic,
  loading,
  isSelected,
  navigateOnSearch = true,
}) => {
  const searchStore = useSearchStore();
  return (
    <TopicPill
      isSelected={isSelected}
      topic={topic}
      loading={loading}
      onClick={() => {
        if (loading) return;

        let topics = [topic];

        if (isSelected) {
          topics = [];
        }

        searchStore.updateAndRunSearch({
          topics: topics,
        });

        if (navigateOnSearch) {
          navigate('/donate/search');
        }
      }}
    />
  );
};

const TopicList = ({
  topics,
  activeTopics = [],
  loading,
  title,
  className = '',
  titleClasses = '',
  navigateOnSearch = true,
}) => {
  // Handle slide change to hide/show arrows
  const [isAtBeginning, setIsAtBeginning] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleSlideChange = (swiper) => {
    setIsAtBeginning(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  };

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [swiperRef, setSwiperRef] = useState(null);
  const { isMobile } = useMobile();

  // Sort topics to put selected ones first
  const sortedTopics = React.useMemo(() => {
    if (!activeTopics.length) return topics;

    const selectedTopics = topics.filter((topic) =>
      activeTopics.includes(topic),
    );
    const unselectedTopics = topics.filter(
      (topic) => !activeTopics.includes(topic),
    );

    return [...selectedTopics, ...unselectedTopics];
  }, [topics, activeTopics]);

  return (
    <>
      {title && (
        <div
          className={`flex justify-between items-center font-agenda-bold text-[22px] leading-none mt-3 mb-5 ${className}`}
        >
          <h2
            className={cx(titleClasses, {
              'text-[22px] leading-none': !titleClasses,
            })}
          >
            {title}
          </h2>
          {swiperRef && swiperRef.allowSlideNext && !isMobile && title && (
            <div className="flex gap-2 items-center justify-center">
              {/* Custom Navigation Buttons */}
              <div
                ref={prevRef}
                onClick={() => swiperRef.slidePrev()}
                className={`py-1.5 px-2.5  flex items-center justify-center border border-solid border-[#adadb6] cursor-pointer rounded-full  swiper-arrows-above-button-prev ${
                  isAtBeginning ? '!cursor-default opacity-40' : ''
                }`}
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  size={'xs'}
                  style={{ color: '#2a2a33', transform: 'translateX(-1px)' }}
                />
              </div>
              <div
                ref={nextRef}
                onClick={() => {
                  swiperRef.slideNext();
                }}
                className={`py-1.5 px-2.5  flex items-center justify-center border border-solid border-[#adadb6] cursor-pointer rounded-full  swiper-arrows-above-button-next ${
                  isAtEnd ? '!cursor-default opacity-40' : ''
                }`}
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  size={'xs'}
                  style={{ color: '#2a2a33', transform: 'translateX(1px)' }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {loading ? (
        <div className="flex gap-x-2">
          {['', '', '', ''].map((_, index) => (
            <TopicListItem key={`loading-${index}`} loading />
          ))}
        </div>
      ) : (
        <Swiper
          className={'event-list'}
          modules={[Navigation]}
          spaceBetween={8}
          slidesPerView={'auto'}
          onSlideChange={handleSlideChange}
          navigation={{
            prevEl: 'swiper-arrows-above-button-prev',
            nextEl: 'swiper-arrows-above-button-next',
          }}
          onInit={(swiper) => {
            setSwiperRef(swiper);
            swiper.prevEl = prevRef.current;
            swiper.nextEl = nextRef.current;
            swiper.navigation.update();
          }}
          onSwiper={setSwiperRef}
        >
          {sortedTopics.map((topic) => (
            <SwiperSlide
              className="my-auto"
              key={topic}
              style={{ width: 'auto' }}
            >
              <TopicListItem
                topic={topic}
                isSelected={activeTopics.includes(topic)}
                navigateOnSearch={navigateOnSearch}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </>
  );
};

export default TopicList;
